/**
 * StairsTypePicker — Sprint 26C.
 *
 * Compact 3-option modal shown right after the user clicks the canvas
 * in 'place:stairs' mode. Picks one of `STAIRS_KINDS` and commits the
 * staircase via the PlanEditorContext.
 *
 * Mirrors the Lab editor's `StairsTypeModal` but uses production tokens
 * + the production context. Mountable once in TabStructure — it self-
 * hides until a `stairsPending` rect exists.
 */
import { X, StretchHorizontal } from 'lucide-react';
import {
  usePlanEditor, STAIRS_KINDS, stairsKindFor,
} from './PlanEditorContext';

const StairsTypePicker = () => {
  const { stairsPending, actions } = usePlanEditor();
  if (!stairsPending) return null;

  const onPick = (kind) => actions.commitStairs(kind);
  const onCancel = () => actions.cancelStairsPending();

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
      data-testid="stairs-type-picker"
      onClick={onCancel}
    >
      <div
        className="w-[420px] max-w-[90vw] rounded-lg border shadow-xl overflow-hidden"
        style={{ background: 'rgba(8,12,20,0.96)',
                 borderColor: 'rgba(168,85,247,0.4)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 px-4 py-3 border-b"
             style={{ borderColor: 'rgba(168,85,247,0.25)' }}>
          <StretchHorizontal className="w-4 h-4 text-violet-400" />
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-[0.2em] text-violet-300/80">
              Nouvel escalier
            </div>
            <div className="text-[12px] text-gray-200">Choisissez le type</div>
          </div>
          <button type="button" onClick={onCancel}
                  className="w-7 h-7 inline-flex items-center justify-center rounded hover:bg-white/5 text-gray-400"
                  data-testid="stairs-picker-cancel-btn"
                  title="Annuler (Échap)">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-3 grid grid-cols-1 gap-2">
          {STAIRS_KINDS.map((k) => (
            <button
              key={k.value}
              type="button"
              onClick={() => onPick(k.value)}
              data-testid={`stairs-picker-kind-${k.value}`}
              className="text-left rounded border px-3 py-2.5 transition-colors border-white/10 hover:border-violet-400/70 hover:bg-violet-500/10 group"
            >
              <div className="text-[11px] font-semibold text-violet-200 group-hover:text-violet-100">
                {k.label}
              </div>
              <div className="text-[10.5px] text-gray-400 group-hover:text-gray-300">
                {k.description}
              </div>
            </button>
          ))}
        </div>
        <div className="px-3 py-2 border-t text-[10px] text-gray-500"
             style={{ borderColor: 'rgba(168,85,247,0.18)' }}>
          Position : <span className="font-mono text-violet-300">{Math.round(stairsKindFor('droit') && stairsPending.x * 100)}% · {Math.round(stairsPending.y * 100)}%</span> · empreinte par défaut redimensionnable après pose
        </div>
      </div>
    </div>
  );
};

export default StairsTypePicker;
